import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { useFullscreenRoute } from "../components/StickyFiltersBar";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";
import { KioskIdleOverlay } from "../components/KioskIdleOverlay";
import { Plus, Trash2, Maximize2, Minimize2, Download, CalendarX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  KIND_OPTIONS,
  OP_ROLES,
  forwardRange,
  emptyForm,
} from "../components/availability/lib";
import { useAvailabilityView } from "../components/availability/useAvailabilityView";
import { AvailabilityFilters } from "../components/availability/AvailabilityFilters";
import { AvailabilityForm } from "../components/availability/AvailabilityForm";

export default function Availability() {
  const { user } = useAuth();
  const isOp = OP_ROLES.includes(user?.role);
  const { pathname } = useLocation();
  const isFs = useFullscreenRoute(pathname);
  useRememberWindowGeometry(isFs ? pathname : null);

  const [rows, setRows] = useState([]);
  const [guards, setGuards] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [guardFilter, setGuardFilter] = useState([]);
  const [kindFilter, setKindFilter] = useState([]);
  const [range, setRange] = useState(() => forwardRange(7)); // default: 1 week forward
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter332 — Batch delete for availability. Keyed by the group's
  // first `id` (rows are groups of contiguous same-kind days). Cleared
  // after every successful load() so stale ids never linger.
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const params = { start: range.start, end: range.end };
      const [r, g, ro] = await Promise.all([
        api.get("/availability", { params }),
        isOp ? api.get("/guards") : Promise.resolve({ data: [] }),
        api.get("/availability/reason-options"),
      ]);
      setRows(r.data || []);
      setGuards((g.data || []).filter((x) => (x.status || "active") !== "inactive"));
      setReasonOptions(ro.data?.reasons || []);
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
    finally { setLoading(false); setSelectedGroups(new Set()); }
  }, [isOp, range.start, range.end]);
  useEffect(() => { load(); }, [load]);
  // iter333 — Fullscreen auto-refresh matches Tardies / Open Shifts
  // cadence (60s) so the availability board is fresh for dispatchers
  // on a wall display.
  useEffect(() => {
    if (!isFs) return;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [isFs, load]);

  const { listedGuards, groupedRows } = useAvailabilityView({
    rows, guards, guardFilter, kindFilter,
  });

  const sortAcc = useMemo(() => ({
    guard: (r) => (r.guard_name || "").toLowerCase(),
    date: (r) => r.start_date || "",
    window: (r) => (r.start_time || "z"),
    kind: (r) => r.kind || "",
  }), []);
  const sortState = useSortableRows(groupedRows, sortAcc, {
    defaultKey: "date", defaultDir: "desc", storageKey: "availability",
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };

  // One-tap "next weekend off" — computes the upcoming Saturday + Sunday and
  // POSTs a single unavailable range so a guard can decline without opening
  // the full form. Only surfaced to guards (ops already have the roster
  // picker to bulk-mark whole crews).
  const [quickAdding, setQuickAdding] = useState(false);
  const nextWeekend = useMemo(() => {
    const now = new Date();
    // JS `getDay()` — Sun=0, Mon=1, …, Sat=6. Aim for the *next* Saturday
    // even if today is already Saturday (avoids the awkward "mark today off
    // at 3pm" case). If today is Sunday, "next Sat" is 6 days out.
    const daysToSat = ((6 - now.getDay()) + 7) % 7 || 7;
    const sat = new Date(now); sat.setDate(now.getDate() + daysToSat);
    const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = `${sat.toLocaleDateString(undefined, { month: "short", day: "numeric" })}–${sun.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    return { start: fmt(sat), end: fmt(sun), label };
  }, []);
  const markWeekendOff = async () => {
    if (quickAdding) return;
    if (!window.confirm(`Mark ${nextWeekend.label} unavailable?`)) return;
    setQuickAdding(true);
    try {
      const r = await api.post("/availability", {
        start_date: nextWeekend.start,
        end_date: nextWeekend.end,
        kind: "unavailable",
        reasons: ["Personal"],
        notes: "Weekend off — quick-add",
      });
      toast.success(`Marked ${r.data?.count || 2} day${(r.data?.count || 2) === 1 ? "" : "s"} unavailable`, {
        description: `${nextWeekend.label} · Dispatch has been notified of any conflicts.`,
      });
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Quick-add failed"));
    } finally {
      setQuickAdding(false);
    }
  };
  const openEdit = (group) => {
    setEditing(group);
    const isReleased = group.kind === "released";
    setForm({
      guard_id: group.guard_id,
      start_date: group.start_date,
      end_date: group.end_date,
      start_time: group.start_time || "",
      end_time: group.end_time || "",
      // Released rows display as "available" — prefill the editor accordingly
      // so the operator can flip to "unavailable" or refine the window/notes.
      kind: isReleased ? "available" : (group.kind || "available"),
      reasons: Array.isArray(group.reasons) && !isReleased ? group.reasons : [],
      notes: isReleased ? "" : (group.notes || ""),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.kind === "unavailable" && form.reasons.length === 0) {
        return toast.error("Pick at least one reason for Unavailable");
      }
      // Editing a released group creates a fresh availability record for the
      // same guard + date range — the overlay logic then hides the released
      // entry and shows this explicit self-report instead.
      const editingReleased = editing && editing.kind === "released";
      if (editing && !editingReleased) {
        // Editing a real (self-reported) group — fan out the patch.
        const patch = {
          kind: form.kind,
          reasons: form.kind === "unavailable" ? form.reasons : [],
          start_time: form.start_time || null,
          end_time: form.end_time || null,
          notes: form.notes || null,
        };
        const ids = editing.ids || [editing.id];
        await Promise.all(ids.map((id) => api.patch(`/availability/${id}`, patch)));
        toast.success(ids.length > 1 ? `Updated ${ids.length} days` : "Updated");
      } else {
        // Fresh create OR override-a-released-row.
        if (isOp && !editingReleased && !form.guard_id) return toast.error("Pick a ");
        if (!form.start_date || !form.end_date) return toast.error("Start & end dates required");
        if (form.end_date < form.start_date) return toast.error("End date must be on or after start date");
        const body = {
          // When overriding a released row we always know the guard_id from
          // the row itself — ops-scoped `isOp` doesn't apply for that path.
          guard_id: editingReleased ? editing.guard_id : (isOp ? form.guard_id : undefined),
          start_date: form.start_date,
          end_date: form.end_date,
          start_time: form.start_time || null,
          end_time: form.end_time || null,
          kind: form.kind,
          reasons: form.kind === "unavailable" ? form.reasons : [],
          notes: form.notes || null,
        };
        const r = await api.post("/availability", body);
        toast.success(editingReleased
          ? `Override saved (${r.data?.count || 1} day${(r.data?.count || 1) === 1 ? "" : "s"})`
          : `Saved ${r.data?.count || 1} entr${(r.data?.count || 1) === 1 ? "y" : "ies"}`);
      }
      setOpen(false); setForm(emptyForm()); setEditing(null); load();
    } catch (err) { toast.error(errorMessage(err, "Save failed")); }
  };

  const removeGroup = async (group) => {
    const n = (group.ids || []).length;
    const confirmMsg = n > 1
      ? `Remove all ${n} days in this range (${group.start_date} → ${group.end_date})?`
      : "Remove this entry?";
    if (!window.confirm(confirmMsg)) return;
    try {
      await Promise.all(group.ids.map((id) => api.delete(`/availability/${id}`)));
      toast.success(n > 1 ? `Removed ${n} days` : "Removed");
      load();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  // iter332 — Bulk delete every availability doc across the selected
  // groups in a single confirmation. Skipping a group means "keep it".
  const bulkDelete = async () => {
    const groups = sortState.sorted.filter((g) => selectedGroups.has(g.ids[0]));
    const allIds = groups.flatMap((g) => g.ids || []);
    if (allIds.length === 0) return;
    const groupCount = groups.length;
    const dayCount = allIds.length;
    const msg = `Delete ${groupCount} ${groupCount > 1 ? "entries" : "entry"} (${dayCount} ${dayCount > 1 ? "days" : "day"} total)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      // Serial in chunks of 8 so we don't burst Mongo, but not
      // per-request either. Any single failure keeps the rest going.
      const results = await Promise.allSettled(allIds.map((id) => api.delete(`/availability/${id}`)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) toast.success(`Deleted ${dayCount} ${dayCount > 1 ? "days" : "day"}`);
      else toast.warning(`Deleted ${dayCount - failed} of ${dayCount}; ${failed} failed`);
      setSelectedGroups(new Set());
      load();
    } catch (e) { toast.error(errorMessage(e, "Bulk delete failed")); }
  };

  // Split-range: remove just one day from a multi-day range. The range on
  // load() will re-group into two sub-ranges around the hole automatically.
  const splitRemoveDay = async (docId, date) => {    if (!window.confirm(`Remove availability for ${date}? The rest of the range stays intact.`)) return;
    try {
      await api.delete(`/availability/${docId}`);
      toast.success(`Removed ${date}`);
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(errorMessage(e, "Remove day failed")); }
  };

  // Split-range: open just one day of a multi-day range in its own single-day
  // editor. The reopened modal targets that day's doc id only, so PATCH-on-save
  // touches only that record.
  const splitEditDay = (docId, date) => {
    if (!editing) return;
    const singleDayGroup = {
      ...editing,
      ids: [docId],
      count: 1,
      start_date: date,
      end_date: date,
    };
    // Rewrite the modal state: close, then re-open with the single-day group.
    // Preserve the original group's kind/reasons/window/notes as a starting
    // point — the operator can flip anything before saving.
    setOpen(false);
    setTimeout(() => openEdit(singleDayGroup), 0);
  };

  const exportCsv = () => {
    // One row per grouped range (mirrors what the user sees on screen).
    // RFC-4180 quoting: wrap every field in double quotes and escape embedded quotes.
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Officer", "Start", "End", "Days", "Kind", "Window Start", "Window End", "Reasons", "Notes"];
    const lines = [headers.map(q).join(",")];
    for (const g of sortState.sorted) {
      const displayKind = g.kind === "released" ? "available (released)" : g.kind;
      const window = g.start_time && g.end_time ? [g.start_time, g.end_time] : ["", ""];
      lines.push([
        g.guard_name || "",
        g.start_date,
        g.count > 1 ? g.end_date : "",
        g.count,
        displayKind,
        window[0],
        window[1],
        (g.reasons || []).join("; "),
        g.notes || (g.kind === "released" && g.released_from_site_name ? `Released from ${g.released_from_site_name}` : ""),
      ].map(q).join(","));
    }
    const csv = "\uFEFF" + lines.join("\r\n");  // BOM so Excel opens UTF-8 correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `availability_${range.start}_to_${range.end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sortState.sorted.length} row${sortState.sorted.length === 1 ? "" : "s"}`);
  };

  const onFs = () => (isFs ? window.close() : window.open("/availability/fullscreen", "_blank", "noopener"));

  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";
  return (
    <div className={containerClass}>
      <KioskIdleOverlay enabled={isFs} label="Availability · Live" />
      {!isFs && (
        <PageHeader
          title="Availability"
          subtitle={
            isOp
              ? "Officer-provided free/busy slots. Dispatchers use this to build the roster."
              : "Your free/busy slots. Dispatchers use this to build the roster — keep it up to date so shifts land on your open days."
          }
          action={
            <div className="flex items-center gap-2" data-testid={isOp ? "availability-actions-op" : "availability-actions-guard"}>
              {!isOp && (
                <span
                  data-testid="availability-scope-pill"
                  className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/5"
                >
                  Your calendar only
                </span>
              )}
              <GhostButton data-testid="availability-export-csv" onClick={exportCsv} title="Download the current grouped view as CSV">
                <Download className="size-3.5" /> Export CSV
              </GhostButton>
              <GhostButton data-testid="availability-fullscreen" onClick={onFs}>
                <Maximize2 className="size-3.5" /> Fullscreen
              </GhostButton>
              {!isOp && (
                <GhostButton
                  data-testid="mark-weekend-off-btn"
                  onClick={markWeekendOff}
                  disabled={quickAdding}
                  title={`Mark ${nextWeekend.label} unavailable in one tap`}
                  className="!border-[#FF9F0A]/50 !text-[#FF9F0A] hover:!bg-[#FF9F0A]/10"
                >
                  <CalendarX className="size-3.5" />
                  {quickAdding ? "Marking…" : `Weekend off (${nextWeekend.label})`}
                </GhostButton>
              )}
              <ActionButton data-testid="add-availability-button" onClick={openCreate}>
                <Plus className="size-4" /> Add
              </ActionButton>
            </div>
          }
        />
      )}
      <div className={isFs ? "p-6 space-y-4" : "p-8 space-y-4"}>
        {isFs && (
          <div className="flex items-center justify-between">
            <div>
              <div className="label-mute">Personnel</div>
              <div className="font-display font-black uppercase text-2xl">Availability</div>
            </div>
            <div className="flex gap-2">
              <ActionButton onClick={openCreate}><Plus className="size-4" /> Add</ActionButton>
              <GhostButton onClick={exportCsv}><Download className="size-3.5" /> Export CSV</GhostButton>
              <GhostButton data-testid="availability-exit-fullscreen" onClick={onFs}>
                <Minimize2 className="size-3.5" /> Close
              </GhostButton>
            </div>
          </div>
        )}

        <AvailabilityFilters
          range={range} setRange={setRange}
          isOp={isOp}
          listedGuards={listedGuards}
          guardFilter={guardFilter} setGuardFilter={setGuardFilter}
          kindFilter={kindFilter} setKindFilter={setKindFilter}
          rowsShown={sortState.sorted.length}
          daysUnderlying={rows.length}
        />

        {selectedGroups.size > 0 && (
          <div
            className="mb-3 border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-4 py-2 flex items-center justify-between"
            data-testid="availability-bulk-toolbar"
          >
            <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-bold">
              {selectedGroups.size} {selectedGroups.size > 1 ? "entries" : "entry"} selected
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="availability-bulk-clear"
                onClick={() => setSelectedGroups(new Set())}
                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1"
              >
                Clear
              </button>
              <button
                type="button"
                data-testid="availability-bulk-delete"
                onClick={bulkDelete}
                className="text-[10px] uppercase tracking-widest bg-[#FF3B30] text-white px-3 py-1 hover:bg-[#FF3B30]/80"
              >
                Delete selected
              </button>
            </div>
          </div>
        )}

        <div
          className="border border-[#27272A] bg-[#121212] overflow-x-auto"
          data-testid="availability-table"
        >
          <table className="w-full min-w-[900px] text-xs">
            <thead className="border-b border-[#27272A]">
              <tr>
                {/* iter332 — Bulk select column. Header toggles all visible rows;
                    each row has its own checkbox. Selection clears when the
                    filter changes any of the visible rows. */}
                <th className="px-3 py-3 text-left w-8">
                  <input
                    type="checkbox"
                    data-testid="availability-select-all"
                    checked={selectedGroups.size > 0 && selectedGroups.size === sortState.sorted.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedGroups.size > 0 && selectedGroups.size < sortState.sorted.length;
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGroups(new Set(sortState.sorted.map((g) => g.ids[0])));
                      } else {
                        setSelectedGroups(new Set());
                      }
                    }}
                    className="accent-[#FF3B30] cursor-pointer"
                  />
                </th>
                <SortableHeader k="guard" state={sortState}>Officer</SortableHeader>
                <SortableHeader k="date" state={sortState}>Start</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">End</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Days</div></th>
                <SortableHeader k="window" state={sortState}>Window</SortableHeader>
                <SortableHeader k="kind" state={sortState}>Kind</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">Reasons</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Notes</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={10} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && sortState.sorted.length === 0 && (
                <tr><td colSpan={10} className="text-center py-16 text-zinc-500 scanlines" data-testid="availability-empty-state">
                  {rows.length > 0
                    ? "No entries match the filters."
                    : (isOp
                        ? "No availability entries in this range."
                        : "You haven't set your availability yet — click Add to declare your free/busy days.")}
                </td></tr>
              )}
              {sortState.sorted.map((g) => (
                <AvailabilityRow
                  key={g.ids.join("|")}
                  group={g}
                  onEdit={openEdit}
                  onDelete={removeGroup}
                  selected={selectedGroups.has(g.ids[0])}
                  onToggleSelect={(rowKey) => {
                    setSelectedGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(rowKey)) next.delete(rowKey);
                      else next.add(rowKey);
                      return next;
                    });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Modal
          title={editing
            ? (editing.kind === "released"
                ? `Override released row (${editing.count > 1 ? `${editing.count} days` : "1 day"})`
                : (editing.count > 1 ? `Edit range (${editing.count} days)` : "Edit availability"))
            : "Add availability"}
          onClose={() => setOpen(false)}
          testId="avail-modal"
        >
          <AvailabilityForm
            form={form}
            setForm={setForm}
            editing={editing}
            isOp={isOp}
            guards={guards}
            reasonOptions={reasonOptions}
            onSubmit={save}
            onCancel={() => setOpen(false)}
            onSplitRemove={splitRemoveDay}
            onSplitEdit={splitEditDay}
          />
        </Modal>
      )}
    </div>
  );
}

// One row of the grouped availability table. Extracted so the parent's
// table body stays compact. Presentational only; state lives on Availability.
function AvailabilityRow({ group, onEdit, onDelete, selected = false, onToggleSelect }) {
  const isReleased = group.kind === "released";
  // Released guards render as "Available" per product spec — the row is
  // still non-editable and gets a subtle amber left-tint to show it came
  // from a shift release rather than a self-report.
  const displayKind = isReleased ? "available" : group.kind;
  const meta = KIND_OPTIONS.find((k) => k.value === displayKind);
  const tone = meta?.tone || "default";
  const testId = group.ids[0];
  const isSingle = group.count === 1;
  return (
    <tr
      data-testid={`availability-row-${testId}`}
      data-day-count={group.count}
      onClick={() => onEdit(group)}
      className={`hover:bg-[#1a1a1a] cursor-pointer ${isReleased ? "bg-[#FF9F0A]/5" : ""} ${selected ? "bg-[#FF3B30]/10" : ""}`}
      title={isReleased
        ? `Released from ${group.released_from_site_name || "shift"} — click to override with an explicit self-report`
        : (isSingle ? "Click to edit" : `Click to edit — spans ${group.count} days`)}
    >
      <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          data-testid={`availability-select-${group.ids[0]}`}
          checked={selected}
          onChange={() => onToggleSelect && onToggleSelect(group.ids[0])}
          className="accent-[#FF3B30] cursor-pointer"
        />
      </td>
      <td className="px-4 py-3 font-medium">{group.guard_name || "—"}</td>
      <td className="px-4 py-3 font-mono">{group.start_date}</td>
      <td className="px-4 py-3 font-mono text-zinc-400">{isSingle ? "—" : group.end_date}</td>
      <td className="px-4 py-3">
        {isSingle ? (
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">1 day</span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] px-1.5 py-0.5">
            {group.count} days
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-zinc-400">
        {group.start_time && group.end_time ? `${group.start_time} – ${group.end_time}` : "All day"}
      </td>
      <td className="px-4 py-3">
        <PillBadge tone={tone}>{displayKind}</PillBadge>
      </td>
      <td className="px-4 py-3 text-zinc-400">
        {Array.isArray(group.reasons) && group.reasons.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {group.reasons.map((tag) => (
              <span key={tag} className="text-[10px] uppercase tracking-widest border border-[#27272A] px-1.5 py-0.5">{tag}</span>
            ))}
          </div>
        ) : "—"}
      </td>
      <td className="px-4 py-3 text-zinc-400 truncate max-w-md">
        {group.notes || (isReleased && group.released_from_site_name ? `Released from ${group.released_from_site_name}` : "—")}
      </td>
      <td className="px-4 py-3 text-right">
        <GhostButton
          data-testid={`avail-delete-${testId}`}
          onClick={(e) => { e.stopPropagation(); onDelete(group); }}
          title={isReleased
            ? "Delete this release record — the guard will no longer show as available from this shift"
            : "Delete this availability entry"}
        >
          <Trash2 className="size-3.5" />
        </GhostButton>
      </td>
    </tr>
  );
}
